import { useEffect, useState } from 'react';
import { B, space, font, radius, weight } from '../../constants/brand';
import { api } from '../../api/client';
import { Modal, ModalFooter, Btn, Spinner, Icon, useToast } from '../ui';
import { formatInstant, isTodayInZone } from '../../utils/format';

// MailTriageModal — the Triage view on the Mail page: the signed-in person's
// own Inbox sorted into "needs you" / "can handle" / "noise" instead of a
// flat list ordered by arrival time.
//
// /me-ONLY, DELIBERATELY, matching the API — this is the person's own
// workload, not a shared mailbox's, so there is no mailbox switcher here
// (contrast the rest of Mail.jsx, which threads ?mailbox= throughout).
//
// A CLASSIFICATION IS A CACHED JUDGEMENT, NOT AN ACTION. This modal never
// moves, deletes, replies to or archives anything on its own — it only reads
// buckets and lets the person open a message (handing off to Mail.jsx's own
// reading pane) or correct a bucket by hand. Anything more is done from the
// message itself, the same "the person clicks the button in front of what it
// acts on" discipline the rest of this page follows.

const BUCKET_META = {
  needsYou:  { label: 'Needs you',  icon: 'warning', color: B.amber,  blurb: 'Only you can decide what to do here.' },
  canHandle: { label: 'Can handle', icon: 'check',   color: B.tealDk, blurb: 'Routine — draft a reply, forward it, or file it.' },
  noise:     { label: 'Noise',      icon: 'archive', color: B.textLt, blurb: 'No action needed.' },
};
const SECTION_ORDER = ['needsYou', 'canHandle', 'noise'];

function fmtWhen(iso) {
  if (!iso) return '';
  return isTodayInZone(iso)
    ? formatInstant(iso, { hour: 'numeric', minute: '2-digit' })
    : formatInstant(iso, { month: 'short', day: 'numeric' });
}

function Row({ m, bucket, onOpen, onOverride, busy }) {
  const meta = BUCKET_META[bucket];
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: space.sm,
      padding: `${space.sm}px ${space.md}px`, borderTop: `1px solid ${B.border}`,
      opacity: busy ? 0.5 : 1,
    }}>
      <button
        onClick={() => onOpen(m)}
        style={{
          flex: 1, minWidth: 0, textAlign: 'left', background: 'none', border: 'none',
          cursor: 'pointer', padding: 0, fontFamily: 'inherit',
        }}
      >
        <div style={{ display: 'flex', gap: space.sm, alignItems: 'baseline' }}>
          <span style={{
            fontSize: font.sm, fontWeight: m.isRead ? weight.regular : weight.bold, color: B.text,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0,
          }}>{m.subject}</span>
          <span style={{ fontSize: font.xs, color: B.textLt, flexShrink: 0 }}>{fmtWhen(m.receivedAt)}</span>
        </div>
        <div style={{ fontSize: font.xs, color: B.textMd, marginTop: 1 }}>
          {(m.from && (m.from.name || m.from.address)) || 'Unknown sender'}
        </div>
        {m.triage?.reason && (
          <div style={{ fontSize: font.xs, color: meta.color, marginTop: 3, fontStyle: 'italic' }}>
            {m.triage.reason}
          </div>
        )}
      </button>
      {/* A quick correction, right on the row — the person reviewing this
          list is exactly the person best placed to say a bucket is wrong,
          and asking them to open the message first to fix it would defeat
          the point of a list that leads with the judgement. */}
      <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
        {SECTION_ORDER.filter((b) => b !== bucket).map((b) => (
          <button
            key={b}
            title={`Move to "${BUCKET_META[b].label}"`}
            disabled={busy}
            onClick={() => onOverride(m.id, b)}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 24, height: 24, borderRadius: radius.lg, border: `1px solid ${B.border}`,
              background: B.surface, color: B.textLt, cursor: busy ? 'default' : 'pointer',
            }}
          ><Icon name={BUCKET_META[b].icon} size={12} /></button>
        ))}
      </div>
    </div>
  );
}

function Section({ bucket, items, onOpen, onOverride, overridingId }) {
  const meta = BUCKET_META[bucket];
  if (!items.length) return null;
  return (
    <div style={{ marginBottom: space.md }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 7,
        padding: `${space.xs}px ${space.md}px`, background: B.surfaceMuted,
        borderTop: `1px solid ${B.border}`, borderBottom: `1px solid ${B.border}`,
      }}>
        <span style={{ display: 'flex', color: meta.color }}><Icon name={meta.icon} size={13} /></span>
        <span style={{ fontSize: font.sm, fontWeight: weight.semibold, color: B.text }}>{meta.label}</span>
        <span style={{ fontSize: font.xs, color: B.textLt }}>({items.length})</span>
        <span style={{ fontSize: font.xs, color: B.textLt, marginLeft: 'auto' }}>{meta.blurb}</span>
      </div>
      {items.map((m) => (
        <Row key={m.id} m={m} bucket={bucket} onOpen={onOpen} onOverride={onOverride} busy={overridingId === m.id} />
      ))}
    </div>
  );
}

export const MailTriageModal = ({ onClose, onOpenMessage }) => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [classifying, setClassifying] = useState(false);
  const [overridingId, setOverridingId] = useState(null);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api.get('mail/triage');
      setData(r);
    } catch (e) { setErr(e.message || 'Could not load your Inbox.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const classify = async () => {
    setClassifying(true);
    try {
      const r = await api.post('mail/triage/classify', {});
      setData(r);
      if (r.classified) toast.success(r.message);
    } catch (e) { toast.error(e.message || 'Could not classify your Inbox.'); }
    finally { setClassifying(false); }
  };

  const override = async (messageRef, bucket) => {
    setOverridingId(messageRef);
    try {
      await api.put(`mail/triage/messages/${encodeURIComponent(messageRef)}`, { bucket });
      // The server is the truth for which section a message belongs in now
      // — a local splice would be one more place this and the API's own
      // grouping logic could quietly disagree.
      await load();
    } catch (e) { toast.error(e.message || 'Could not move that message.'); }
    finally { setOverridingId(null); }
  };

  const unclassified = data?.counts?.unclassified || 0;
  const total = data?.counts?.total || 0;

  return (
    <Modal title="Inbox triage" width={720} onClose={onClose}>
      {loading ? (
        <div style={{ padding: space.xl, textAlign: 'center' }}><Spinner label="Reading your Inbox…" /></div>
      ) : err ? (
        <div style={{ padding: space.lg, color: B.red, fontSize: font.sm }}>{err}</div>
      ) : data?.available === false ? (
        <div style={{ padding: space.lg, color: B.textMd, fontSize: font.sm }}>{data.error}</div>
      ) : total === 0 ? (
        <div style={{ padding: space.xl, textAlign: 'center', color: B.textMd, fontSize: font.sm }}>
          Your Inbox is empty.
        </div>
      ) : (
        <>
          <div style={{
            display: 'flex', alignItems: 'center', gap: space.sm,
            padding: `0 ${space.md}px ${space.sm}px`, fontSize: font.xs, color: B.textMd,
          }}>
            {unclassified > 0 ? (
              <span>
                <strong>{unclassified}</strong> of {total} message{total === 1 ? '' : 's'} not yet classified.
              </span>
            ) : (
              <span>All {total} message{total === 1 ? '' : 's'} classified.</span>
            )}
          </div>
          <div style={{ maxHeight: '60vh', overflowY: 'auto', border: `1px solid ${B.border}`, borderRadius: radius.lg }}>
            {SECTION_ORDER.map((b) => (
              <Section key={b} bucket={b} items={data[b] || []} onOpen={onOpenMessage} onOverride={override} overridingId={overridingId} />
            ))}
            {unclassified > 0 && (
              <div style={{ padding: `${space.sm}px ${space.md}px`, borderTop: `1px solid ${B.border}`, fontSize: font.sm, color: B.textMd }}>
                {unclassified} message{unclassified === 1 ? '' : 's'} not yet classified — press "Classify now" below.
              </div>
            )}
          </div>
        </>
      )}
      <ModalFooter>
        <Btn variant="ghost" onClick={onClose}>Close</Btn>
        {total > 0 && (
          <Btn onClick={classify} loading={classifying} disabled={classifying || unclassified === 0}>
            <Icon name="sync" size={14} /> Classify now
          </Btn>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default MailTriageModal;
